import { derived } from 'svelte/store';
import i18n, { type Config } from 'sveltekit-i18n';

import { dev } from '$app/environment';
import { resolveRoute } from '$app/paths';

import { ƒ_about, ƒ_home, ƒ_linkInBio, ƒ_photograph, ƒ_photographs, ƒ_thought, ƒ_thoughts, type InventoryRoute } from '$lib/routes';

import it from './it';
import en from './en';

export type Locale = 'it' | 'en';

export const defaultLocale = 'it';

export const config: Config = {
	log: {
		level: dev ? 'warn' : 'error'
	},
	translations: {
		it,
		en
	},
	loaders: [
		{
			locale: 'it',
			key: 'menu',
			loader: async () => (await import('./it/menu.json')).default
		},
		{
			locale: 'en',
			key: 'menu',
			loader: async () => (await import('./en/menu.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_about.id],
			key: 'about',
			loader: async () => (await import('./it/about.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_about.id],
			key: 'about',
			loader: async () => (await import('./en/about.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_linkInBio.id],
			key: 'linkInBio',
			loader: async () => (await import('./it/linkInBio.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_linkInBio.id],
			key: 'linkInBio',
			loader: async () => (await import('./en/linkInBio.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_thoughts.id],
			key: 'thoughts',
			loader: async () => (await import('./it/thoughts.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_thoughts.id],
			key: 'thoughts',
			loader: async () => (await import('./en/thoughts.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_home.id, ƒ_thought.id],
			key: 'thought',
			loader: async () => (await import('./it/thought.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_home.id, ƒ_thought.id],
			key: 'thought',
			loader: async () => (await import('./en/thought.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_photographs.id, ƒ_linkInBio.id],
			key: 'photographs',
			loader: async () => (await import('./it/photographs.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_photographs.id, ƒ_linkInBio.id],
			key: 'photographs',
			loader: async () => (await import('./en/photographs.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_photograph.id],
			key: 'photograph',
			loader: async () => (await import('./it/photograph.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_photograph.id],
			key: 'photograph',
			loader: async () => (await import('./en/photograph.json')).default
		},
		{
			locale: 'it',
			routes: [ƒ_home.id],
			key: 'home',
			loader: async () => (await import('./it/home.json')).default
		},
		{
			locale: 'en',
			routes: [ƒ_home.id],
			key: 'home',
			loader: async () => (await import('./en/home.json')).default
		}
	]
};

export const { t, loading, locales, locale, translations, loadTranslations, addTranslations, setLocale, setRoute } = new i18n(config);

export const path = derived([translations, locale], ([$translations, $locale]) => (route: InventoryRoute, params: Record<string, string> = {}) => {
	const locale = params.locale || $locale;

	if (!$translations[locale][`routes.${route.id}`]) return null;

	return resolveRoute($translations[locale][`routes.${route.id}`], { locale, ...params });
});
